import type {
  Asset,
  Project,
  VisualMatch as VisualMatchType,
} from '@frameio/graphql-client/generated';
import { getLink } from '@frameio/shared/routing';
import type { useSetAssetClipMatch } from '../../hooks/useAssetClipMatch';
import { isAssetItem, isProjectItem } from '../../utils';
import { prepareAssetClipMatch } from './prepareAssetClipMatch';

export const getResultItemLink = (
  item: Asset | Project,
  type: 'AssetResult' | 'ProjectResult' | undefined,
  visualMatch?: VisualMatchType | null,
  setAssetClipMatch?: ReturnType<typeof useSetAssetClipMatch>
) => {
  const isRecentlyDeleted = Boolean(item?.deletedAt);

  if (isAssetItem(item, type)) {
    if (visualMatch && visualMatch.clips?.[0]) {
      const { currentTime, duration } = prepareAssetClipMatch({
        assetId: item.id,
        visualMatch,
        activeClipIndex: 0,
        setAssetClipMatch,
      });
      return getAssetResultItemLink(item, {
        isRecentlyDeleted,
        currentTime,
        duration,
      });
    }

    return getAssetResultItemLink(item, {
      isRecentlyDeleted,
    });
  }

  if (isProjectItem(item, type)) {
    return getProjectResultItemLink(item);
  }

  return '';
};

export const getAssetResultItemLink = (
  asset: Asset,
  opts: {
    isRecentlyDeleted?: boolean;
    currentTime?: number;
    duration?: number;
    commentId?: string;
  } = {}
): string => {
  const { isRecentlyDeleted, currentTime, duration, commentId } = opts;
  const projectId = asset.project?.id ?? '';
  const isVersionStack = Number.isInteger(asset?.versionNumber);

  // Guard against missing projectId - return empty string if not available
  if (!projectId) {
    return '';
  }

  if (isRecentlyDeleted) {
    const directParent = asset.parent;

    // Skip version stack container to get to the parent folder
    const parentFolder =
      directParent && 'parent' in directParent && directParent.parent
        ? directParent.parent
        : directParent;

    // Check if parent folder is deleted and not root
    const isParentDeleted = Boolean(parentFolder?.deletedAt);
    const ancestorFolders = asset.ancestorFolders || [];
    const lastAncestor = ancestorFolders[ancestorFolders.length - 1];
    const isParentNotRoot = lastAncestor && lastAncestor.name !== 'root';

    if (isParentDeleted && isParentNotRoot && parentFolder?.id) {
      return getLink({
        page: 'trash',
        projectId,
        folderAssetId: parentFolder.id,
      });
    }

    // Otherwise, go to recently-deleted root
    return getLink({
      page: 'trash',
      projectId,
    });
  }

  if (asset?.assetType === 'FOLDER') {
    return getLink({
      page: 'folder',
      folderAssetId: asset.id,
      projectId,
    });
  }

  const assetId = isVersionStack && asset?.parent ? asset.parent.id : asset.id;
  const versionId = isVersionStack ? asset.id : undefined;

  return getLink({
    page: 'folderViewer',
    projectId,
    assetId,
    versionId,
    t: currentTime,
    d: duration,
    commentId,
  });
};

export const getProjectResultItemLink = (project: Project): string => {
  return getLink({
    page: 'folder',
    projectId: project.id,
  });
};
